import { byLang, type MdModule } from '../byLang';

export type PageAttributes = {
  title?: string;
  description?: string;
  section?: string;
  draft?: string;
};

// Eager + attributes-only: frontmatter for every page, in every language, with
// no HTML — small enough to bundle upfront for listing/search/nav (unlike
// `pages` below, which lazy-loads a page's full HTML only once it's opened).
const rawAttributes = byLang(
  import.meta.glob<PageAttributes>('./*/*.md', {
    eager: true,
    import: 'attributes',
  }),
);

// Frontmatter values are machine-translated (see docs/README.md), so a boolean
// flag can't be trusted per-language — a page is a draft only per its `en`
// source, regardless of what the active locale's copy says.
const draftSlugs = new Set(
  Object.entries(rawAttributes)
    .filter(
      ([, langs]) =>
        langs.en?.draft &&
        langs.en?.draft !== 'false' &&
        langs.en?.draft !== '0',
    )
    .map(([slug]) => slug),
);

// Drafts are unpublished: excluded here so they have no route (no URL) and
// never appear in the guide listing/sidebar, without callers needing to know
// about the `draft` flag at all.
const publish = <T>(byLangMap: Record<string, Partial<Record<string, T>>>) =>
  Object.fromEntries(
    Object.entries(byLangMap).filter(([slug]) => !draftSlugs.has(slug)),
  );

export const pagesAttributes = publish(rawAttributes);

export const pages = publish(byLang(import.meta.glob<MdModule>('./*/*.md')));
